/**
 * BannerProvider compartido para microservicios A365
 * 
 * Proporciona gestión unificada de banners/promociones
 * usando los observables de @a365/api, con soporte para
 * diferentes estrategias de obtención de datos.
 */

import React, { createContext, useContext, useEffect, useState } from "react";
import { getPromo, promo$ } from "@a365/api";

const BannerContext = createContext();

export const useBanner = () => {
  const context = useContext(BannerContext);
  if (!context) {
    throw new Error('useBanner debe utilizarse dentro de un BannerProvider');
  }
  return context;
};

/**
 * Estrategias para obtener datos de promociones
 */
export const BANNER_STRATEGIES = {
  // Automático: usa geolocation.country_id cuando esté disponible
  AUTO: 'auto',
  // Manual: requiere country_id específico
  MANUAL: 'manual',
  // LocalStorage: usa country_id del localStorage
  STORAGE: 'storage'
};

export const BannerProvider = ({ 
  children, 
  strategy = BANNER_STRATEGIES.AUTO,
  countryId = null,
  geolocation = null 
}) => {
  const [bannerData, setBannerData] = useState(null);

  // Efecto para suscribirse a promo$
  useEffect(() => {
    const subscription = promo$.subscribe((data) => {
      setBannerData(data);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Efecto para obtener promociones según la estrategia
  useEffect(() => {
    let targetCountryId = null;

    switch (strategy) {
      case BANNER_STRATEGIES.AUTO:
        if (geolocation?.country_id) {
          targetCountryId = geolocation.country_id;
        }
        break;
      
      case BANNER_STRATEGIES.MANUAL:
        if (countryId) {
          targetCountryId = countryId;
        }
        break;
      
      case BANNER_STRATEGIES.STORAGE:
        const storedCountryId = localStorage.getItem("country_id");
        if (storedCountryId) {
          targetCountryId = Number(storedCountryId);
        }
        break;
      
      default:
        targetCountryId = 164; // Fallback por defecto
    }

    if (targetCountryId) {
      getPromo(targetCountryId);
    }
  }, [strategy, countryId, geolocation]);

  const contextValue = {
    bannerData,
  };

  return (
    <BannerContext.Provider value={contextValue}>
      {children}
    </BannerContext.Provider>
  );
};

// Re-exportar el contexto
export { BannerContext };
